/**
 * `/analytics` — admin Analytics dashboard (#24).
 *
 * Layout (matches the issue brief):
 *
 *   1. Header: site selector dropdown + date-range picker (default last
 *      30 days).
 *   2. KPI tiles: total page views, unique visitors, sessions, bot view
 *      percentage. Stack vertically on xs/sm.
 *   3. Daily bar chart of page views — hand-rolled inline SVG (no charting
 *      library, per slice 3 #19 precedent).
 *   4. Top pages table — path, total views, % of total.
 *   5. Bot-vs-human stacked horizontal bar.
 *   6. AI scraper breakdown (last 7 days) — UA → count.
 *   7. Referrer breakdown (last 7 days) — referrer → count.
 *
 * Responsive:
 *   - 360 / 768 / 1280: KPI tiles stack via MUI `<Stack direction>`
 *     responsive prop. Tables wrap inside `<TableContainer>` so narrow
 *     viewports scroll horizontally rather than overflowing the layout.
 *
 * Loading / error / empty states:
 *   - Loading → MUI `<Skeleton>` blocks for the KPI row + chart + tables.
 *   - Error  → MUI `<Alert severity="error">` with the API message.
 *   - Empty  → individual tiles / tables render their own empty captions
 *     so a quiet day still produces a sensible page.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type {
  AdminAnalyticsResponse,
  AdminAnalyticsTopPage,
  AdminAnalyticsAiScraperRow,
  AdminAnalyticsReferrerRow,
  SiteResponse,
} from "@ottopilot/shared";
import { useSites } from "../hooks/useSites";
import { useAnalyticsDashboard } from "../hooks/useAnalytics";
import { defaultDateRange } from "../features/analytics/dateRange";
import {
  BotHumanRatioBar,
  DailyBarChart,
  type DailyBarPoint,
} from "../features/analytics/AnalyticsCharts";

/** Page size used by the sites dropdown lookup. The selector lists every site the
 *  operator can see; for MVP all sites are accessible to authenticated users. */
const SITES_DROPDOWN_PAGE_SIZE = 100;

/**
 * Numeric tile for the KPI row. Mirrors `AiUsagePage`'s NumericTile so the
 * two dashboards share a visual grammar.
 */
function NumericTile({
  label,
  value,
  caption,
  testId,
}: {
  label: string;
  value: string;
  caption?: string;
  testId: string;
}): ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography variant="h2" sx={{ mt: 0.5, fontWeight: 500 }} data-testid={testId}>
        {value}
      </Typography>
      {caption ? (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      ) : null}
    </Paper>
  );
}

/**
 * Sum daily rows into top-line KPI numbers. Pure function so the unit
 * tests can drive it without rendering the whole page.
 */
export function computeKpis(data: AdminAnalyticsResponse | undefined): {
  totalPageViews: number;
  uniqueVisitors: number;
  sessions: number;
  botViewPct: number;
} {
  if (!data) {
    return { totalPageViews: 0, uniqueVisitors: 0, sessions: 0, botViewPct: 0 };
  }
  let totalPageViews = 0;
  let uniqueVisitors = 0;
  let sessions = 0;
  for (const row of data.daily) {
    totalPageViews += row.pageViews;
    uniqueVisitors += row.uniqueVisitors;
    sessions += row.sessions;
  }
  const totalForRatio = data.botVsHuman.humanViews + data.botVsHuman.botViews;
  const botViewPct =
    totalForRatio === 0 ? 0 : Math.round((data.botVsHuman.botViews / totalForRatio) * 100);
  return { totalPageViews, uniqueVisitors, sessions, botViewPct };
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPct(n: number, total: number): string {
  if (total === 0) return "0%";
  return `${Math.round((n / total) * 100)}%`;
}

function TopPagesTable({
  rows,
  totalViews,
}: {
  rows: ReadonlyArray<AdminAnalyticsTopPage>;
  totalViews: number;
}): ReactElement {
  return (
    <TableContainer>
      <Table aria-label="Top pages" data-testid="analytics-top-pages-table">
        <TableHead>
          <TableRow>
            <TableCell>Path</TableCell>
            <TableCell align="right">Views</TableCell>
            <TableCell align="right">% of total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ py: 6 }}
                data-testid="analytics-top-pages-empty"
              >
                <Typography variant="body2" color="text.secondary">
                  No page-view data in this range yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.pageId}
                hover
                data-testid="analytics-top-pages-row"
                data-page-id={row.pageId}
              >
                <TableCell data-testid="analytics-top-pages-row-path">
                  <Typography variant="body2">{row.path}</Typography>
                </TableCell>
                <TableCell align="right" data-testid="analytics-top-pages-row-views">
                  {formatNumber(row.totalViews)}
                </TableCell>
                <TableCell align="right">{formatPct(row.totalViews, totalViews)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ScraperBreakdownTable({
  rows,
}: {
  rows: ReadonlyArray<AdminAnalyticsAiScraperRow>;
}): ReactElement {
  return (
    <TableContainer>
      <Table aria-label="AI scraper breakdown" data-testid="analytics-scraper-table">
        <TableHead>
          <TableRow>
            <TableCell>User-Agent</TableCell>
            <TableCell align="right">Requests (last 7 days)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={2}
                align="center"
                sx={{ py: 6 }}
                data-testid="analytics-scraper-empty"
              >
                <Typography variant="body2" color="text.secondary">
                  No AI scraper traffic in the last 7 days.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.ua} data-testid="analytics-scraper-row">
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.ua}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(row.count)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ReferrerBreakdownTable({
  rows,
}: {
  rows: ReadonlyArray<AdminAnalyticsReferrerRow>;
}): ReactElement {
  return (
    <TableContainer>
      <Table aria-label="Referrer breakdown" data-testid="analytics-referrer-table">
        <TableHead>
          <TableRow>
            <TableCell>Referrer</TableCell>
            <TableCell align="right">Visits (last 7 days)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={2}
                align="center"
                sx={{ py: 6 }}
                data-testid="analytics-referrer-empty"
              >
                <Typography variant="body2" color="text.secondary">
                  No referrer data in the last 7 days.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.referrer} data-testid="analytics-referrer-row">
                <TableCell sx={{ maxWidth: 480 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.referrer}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(row.count)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function dailyChartPoints(data: AdminAnalyticsResponse | undefined): DailyBarPoint[] {
  if (!data) return [];
  return data.daily.map((row) => ({ date: row.date, value: row.pageViews }));
}

export function AnalyticsPage(): ReactElement {
  // Default range = last 30 days, computed once per mount. The user can
  // override via the date inputs; we don't memoise the default because the
  // `useState` initialiser handles the one-time computation.
  const [range, setRange] = useState(() => defaultDateRange());

  // List of sites the operator can pick. We default to "no selection" and
  // auto-pick the first site once the list resolves so the dashboard is
  // useful on first paint instead of asking the user to pick.
  const sites = useSites({ page: 1, pageSize: SITES_DROPDOWN_PAGE_SIZE });
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);
  const effectiveSiteId = useMemo<string | undefined>(() => {
    if (selectedSiteId) return selectedSiteId;
    return sites.data?.data[0]?.id;
  }, [selectedSiteId, sites.data]);

  const dashboard = useAnalyticsDashboard({
    siteId: effectiveSiteId,
    from: range.from,
    to: range.to,
  });

  const kpis = useMemo(() => computeKpis(dashboard.data), [dashboard.data]);
  const chartPoints = useMemo(() => dailyChartPoints(dashboard.data), [dashboard.data]);

  // TanStack Query keeps `isPending = true` for disabled queries (no data
  // yet). We only treat it as "loading" when the query is actually
  // enabled, otherwise the empty-state branch below renders the right
  // copy for "no site picked" / "no sites at all".
  const isLoading = dashboard.isFetching && Boolean(effectiveSiteId);
  const showEmptyState = !isLoading && !dashboard.isError && (!effectiveSiteId || !dashboard.data);

  return (
    <Stack spacing={3} data-testid="analytics-page">
      <Box>
        <Typography variant="h2">Analytics</Typography>
        <Typography variant="body2" color="text.secondary">
          Per-site dashboard. Historic numbers come from the daily roll-up; the AI scraper and
          referrer breakdowns reflect raw events from the last 7 days.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} data-testid="analytics-controls">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl
            sx={{ minWidth: { xs: "100%", md: 280 } }}
            size="small"
            disabled={sites.isPending}
          >
            <InputLabel id="analytics-site-select-label">Site</InputLabel>
            <Select
              labelId="analytics-site-select-label"
              label="Site"
              value={effectiveSiteId ?? ""}
              onChange={(e) => setSelectedSiteId(String(e.target.value) || undefined)}
              data-testid="analytics-site-select"
            >
              {(sites.data?.data ?? []).map((site: SiteResponse) => (
                <MenuItem key={site.id} value={site.id} data-testid="analytics-site-option">
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="From"
            type="date"
            size="small"
            value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            data-testid="analytics-from-input"
            sx={{ minWidth: { xs: "100%", md: 180 } }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            data-testid="analytics-to-input"
            sx={{ minWidth: { xs: "100%", md: 180 } }}
          />
        </Stack>
      </Paper>

      {dashboard.isError ? (
        <Alert severity="error" data-testid="analytics-error">
          {dashboard.error.message}
        </Alert>
      ) : null}

      {isLoading ? (
        <Stack spacing={2} data-testid="analytics-loading">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Skeleton variant="rounded" height={120} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={120} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={120} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={120} sx={{ flex: 1 }} />
          </Stack>
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={260} />
        </Stack>
      ) : null}

      {!isLoading && showEmptyState ? (
        <Alert severity="info" data-testid="analytics-empty">
          {sites.data?.data.length === 0
            ? "Create a Site to start collecting analytics."
            : "Pick a site to see its analytics."}
        </Alert>
      ) : null}

      {!isLoading && dashboard.data ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} data-testid="analytics-kpis">
            <NumericTile
              label="Page views"
              value={formatNumber(kpis.totalPageViews)}
              caption="across the range"
              testId="analytics-kpi-page-views"
            />
            <NumericTile
              label="Unique visitors"
              value={formatNumber(kpis.uniqueVisitors)}
              caption="from daily rollup"
              testId="analytics-kpi-unique-visitors"
            />
            <NumericTile
              label="Sessions"
              value={formatNumber(kpis.sessions)}
              caption="from daily rollup"
              testId="analytics-kpi-sessions"
            />
            <NumericTile
              label="Bot views"
              value={`${kpis.botViewPct}%`}
              caption="of total views"
              testId="analytics-kpi-bot-pct"
            />
          </Stack>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" gutterBottom>
              Daily page views
            </Typography>
            <DailyBarChart data={chartPoints} testId="analytics-daily-chart" />
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
              <Typography variant="subtitle2">Top pages</Typography>
              <Typography variant="caption" color="text.secondary">
                Most-viewed pages across the range
              </Typography>
            </Box>
            <TopPagesTable rows={dashboard.data.topPages} totalViews={kpis.totalPageViews} />
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" gutterBottom>
              Bot vs human traffic
            </Typography>
            <BotHumanRatioBar
              humanViews={dashboard.data.botVsHuman.humanViews}
              botViews={dashboard.data.botVsHuman.botViews}
              testId="analytics-bot-human-ratio"
            />
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
              <Typography variant="subtitle2">AI scraper traffic</Typography>
              <Typography variant="caption" color="text.secondary">
                Last 7 days, by user-agent
              </Typography>
            </Box>
            <ScraperBreakdownTable rows={dashboard.data.aiScraperBreakdown} />
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
              <Typography variant="subtitle2">Top referrers</Typography>
              <Typography variant="caption" color="text.secondary">
                Last 7 days, human visits only
              </Typography>
            </Box>
            <ReferrerBreakdownTable rows={dashboard.data.referrerBreakdown} />
          </Paper>
        </>
      ) : null}
    </Stack>
  );
}
